import { cn } from '@/lib/utils';

const BAR_WIDTH = 24;
const BAR_RADIUS = 4;
const DEFAULT_HEIGHT = 200;
/** Fixed left gutter reserved for the y-axis tick labels (0 / half / max) —
 * the plot area (columns, start rule, month/value labels) is confined to
 * the space after it, so a tall first column can never sit on top of a
 * tick label at any width. */
const GUTTER = 48;

function defaultFormat(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

/**
 * CSS `calc()` for "GUTTER + fraction of the plot width (100% minus the
 * gutter)", expressed relative to the chart's own 100% (the outer SVG has
 * no viewBox, so percentages resolve against its real rendered width).
 * A single mixed px/% calc() term — no nested SVG viewport, which does not
 * reliably pick up a CSS `width: calc(...)` on a non-root `<svg>` across
 * browsers. `fraction` is 0 (left edge of the plot) to 1 (right edge).
 */
function plotX(fraction: number): string {
  const pxTerm = GUTTER * (1 - fraction);
  const pctTerm = fraction * 100;
  return `calc(${pxTerm}px + ${pctTerm}%)`;
}

/**
 * Column path in LOCAL coordinates: (0,0) is the top-left of the bar,
 * (width, height) is the bottom-right (the baseline). Only the top two
 * corners are rounded — the baseline stays square. Positioned later via a
 * CSS transform, never a bare `rx` (which would round all four corners).
 */
function columnPath(width: number, height: number, radius: number): string {
  if (height <= 0) return '';
  const r = Math.min(radius, width / 2, height);
  return [
    `M 0 ${height}`,
    `L 0 ${r}`,
    `Q 0 0 ${r} 0`,
    `L ${width - r} 0`,
    `Q ${width} 0 ${width} ${r}`,
    `L ${width} ${height}`,
    'Z',
  ].join(' ');
}

export interface MonthlyChartProps {
  /** Mono kicker above the chart. */
  title: string;
  months: string[];
  values: number[];
  /** Index into months/values where the accent color begins; columns before are white/20. */
  startIndex?: number;
  /** Label beside the "we start" rule at startIndex. */
  startLabel?: string;
  /** Small mono note under the chart, e.g. "Spark Ads, paid". */
  note?: string;
  /** Value formatter for labels, axis ticks and the sr-only table. */
  format?: (n: number) => string;
  className?: string;
  /** Fixed SVG height in px — keeps the chart legible at 390px regardless of column count. */
  height?: number;
}

/**
 * Single-series month-by-month column chart. Fixed-height inline SVG, no
 * chart library. Columns are a fixed 24px wide and positioned at a
 * percentage x (via a `calc()` CSS transform — see `plotX`), so they stay a
 * constant width across any container size instead of stretching with a
 * viewBox, and never start before the reserved left gutter that carries the
 * y-axis tick labels. Mark spec lives in the `dataviz` skill; page-specific
 * numbers and copy in `socialAiData.ts`.
 */
export function MonthlyChart({
  title,
  months,
  values,
  startIndex,
  startLabel = 'we start',
  note,
  format = defaultFormat,
  className,
  height = DEFAULT_HEIGHT,
}: MonthlyChartProps) {
  const n = months.length;
  const maxVal = Math.max(...values, 0);
  // Extra top headroom (vs. the plot's own top) so the "we start" annotation
  // always has its own row above the tallest column's value label, even
  // when the max/last-labeled column is the first one after the start line
  // (e.g. TikTok, where June is both the start month and the max).
  const topPad = 40;
  const bottomPad = 26;
  const plotTop = topPad;
  const baseline = height - bottomPad;
  const plotHeight = Math.max(baseline - plotTop, 1);

  const barHeight = (v: number) => (maxVal > 0 ? (v / maxVal) * plotHeight : 0);

  const maxIndex = values.indexOf(maxVal);
  const lastIndex = values.length - 1;
  const labelIndices = new Set([maxIndex, lastIndex]);

  const startFraction =
    startIndex != null && startIndex > 0 && startIndex < n ? startIndex / n : null;

  const ariaLabel = `${title}: ${months.map((m, i) => `${m} ${format(values[i])}`).join(', ')}`;

  return (
    <div className={cn('flex flex-col', className)}>
      <p className="truncate whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
        {title}
      </p>
      <svg role="img" aria-label={ariaLabel} width="100%" height={height} className="mt-4 overflow-visible">
        {/* y-axis tick labels: 0 / half / max, right-aligned in the reserved
            left gutter. Columns never enter [0, GUTTER), so they can never
            cover them. Mid tick only once charts have room (>= xl, where
            every grid on this page reaches a wide-enough column). */}
        {[0, maxVal / 2, maxVal].map((tick, i) => {
          const y = baseline - (tick / (maxVal || 1)) * plotHeight;
          return (
            <g key={i}>
              <line x1={GUTTER} x2="100%" y1={y} y2={y} stroke="rgba(255,255,255,0.1)" strokeWidth={1} />
              <text
                x={GUTTER - 8}
                y={y - 5}
                textAnchor="end"
                className={cn('fill-white/35 font-mono text-[9px]', i === 1 && 'hidden xl:block')}
              >
                {format(tick)}
              </text>
            </g>
          );
        })}

        {/* optional "we start" rule, its own row above the value labels */}
        {startFraction != null && (
          <g>
            <line
              x1={plotX(startFraction)}
              x2={plotX(startFraction)}
              y1={plotTop - 20}
              y2={baseline}
              stroke="var(--color-accent)"
              strokeOpacity={0.4}
              strokeWidth={1}
              strokeDasharray="2 3"
            />
            {/* anchored "end" so the label grows back into the (typically
                lower) pre-start columns, away from the value label on the
                column that usually sits right after the start line */}
            <text
              x={0}
              y={plotTop - 26}
              dx={-4}
              textAnchor="end"
              style={{ transform: `translateX(${plotX(startFraction)})` }}
              className="fill-white/45 font-mono text-[9px] uppercase tracking-[0.15em]"
            >
              {startLabel}
            </text>
          </g>
        )}

        {/* columns — one per value, top-rounded, square at the baseline */}
        {values.map((v, i) => {
          const fraction = (i + 0.5) / n;
          const h = barHeight(v);
          const isAfterStart = startIndex == null || i >= startIndex;
          return (
            <path
              key={`${months[i]}-${i}`}
              data-col
              d={columnPath(BAR_WIDTH, h, BAR_RADIUS)}
              fill={isAfterStart ? 'var(--color-accent)' : 'rgba(255,255,255,0.2)'}
              style={{
                transform: `translateX(${plotX(fraction)}) translateX(-${BAR_WIDTH / 2}px) translateY(${baseline - h}px)`,
              }}
            />
          );
        })}

        {/* value labels: the max column and the last column only.
            - The last label is right-anchored (own column's right edge) so
              it never bleeds past the chart's right edge, and its y clears
              its immediate left neighbor's height too (it grows leftward,
              so a tall neighbor's column body is otherwise in its way).
            - When the max column IS that immediate left neighbor, both
              labels end up pinned to the same ceiling (plotTop - 8) with
              nowhere higher to go — so the max label also flips to
              anchor "end" (grows left, away from the last label) instead
              of sitting centered over a column it's about to touch. */}
        {values.map((v, i) => {
          if (!labelIndices.has(i)) return null;
          const fraction = (i + 0.5) / n;
          const h = barHeight(v);
          const isLast = i === lastIndex;
          const isMax = i === maxIndex;
          const adjacentToLast = isMax && i === lastIndex - 1;
          const neighborH = isLast && i > 0 ? barHeight(values[i - 1]) : 0;
          const y = Math.max(baseline - Math.max(h, neighborH) - 8, plotTop - 8);
          const leansEnd = isLast || adjacentToLast;
          const xOffset = isLast ? BAR_WIDTH / 2 : 0;
          return (
            <text
              key={`label-${i}`}
              x={0}
              y={y}
              textAnchor={leansEnd ? 'end' : 'middle'}
              style={{ transform: `translateX(${plotX(fraction)}) translateX(${xOffset}px)` }}
              className="fill-white/70 font-mono text-[10px]"
            >
              {format(v)}
            </text>
          );
        })}

        {/* month labels */}
        {months.map((m, i) => {
          const fraction = (i + 0.5) / n;
          return (
            <text
              key={`month-${i}`}
              x={0}
              y={baseline + 16}
              textAnchor="middle"
              style={{ transform: `translateX(${plotX(fraction)})` }}
              className="fill-white/40 font-mono text-[9px] uppercase tracking-[0.1em]"
            >
              {m}
            </text>
          );
        })}
      </svg>
      {note && <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.18em] text-white/35">{note}</p>}
      <table className="sr-only">
        <caption>{title}</caption>
        <thead>
          <tr>
            <th>Month</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          {months.map((m, i) => (
            <tr key={m}>
              <td>{m}</td>
              <td>{format(values[i])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
